import {
  inject,
  OnInit,
  Component,
  ViewChild,
  DestroyRef,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { CopyFeedParams } from 'src/app/api/services/feed-api.service';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';

import { DialogCloseOptions, DialogCloseReason } from '@sui/dialog';

import { FeedDuplicateFormComponent } from './feed-duplicate-form/feed-duplicate-form.component';

import { TabConfig, SearchableDialog } from 'src/app/shared/utils';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';

import { NomObjectClass, NomObjectTypes } from 'src/app/core/constants';
import { DataObject } from 'src/app/core/models/core.models';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';

import { FeedPersistenceService } from 'src/app/core/services/feed-persistence.service';

import { forkJoin, tap } from 'rxjs';

interface DialogData extends TabDialogDataBase {
  objectsData: ObjectsData[];
}

interface DialogResult {}

export interface ObjectsData {
  id: string;
  classId: NomObjectClass;
}

@Component({
  selector: 'app-feed-duplicate-dialog',
  templateUrl: './feed-duplicate-dialog.component.html',
  styleUrl: './feed-duplicate-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FeedDuplicateDialogComponent
  extends TabDialogBase<DialogData, DialogResult>
  implements SearchableDialog, OnInit
{
  protected destroyRef = inject(DestroyRef);
  private utilsDataObjectApi = inject(UtilsDataObjectApiService);
  private feedPersistence = inject(FeedPersistenceService);

  // TODO: angular v20 has this feature without decorator and it is signal based
  // TODO: we need some day to migrate to that
  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  // * Search Manager
  private tabSearch = this.enableTabSearch();
  onSearchInput = this.tabSearch.onSearchInput;

  objectActionIcon: IconProp = faCopy;
  saveButtonIcon: IconProp = faCopy;
  objectIcon: IconProp;
  objectActionTitle = '';
  objectTitle = '';

  hasDimensionInSelection: boolean = false;
  uniqueObjectTypeIdCount: number = 0;
  objects: DataObject[] = [];

  get searchString$() {
    return this.tabSearch.searchString$;
  }

  get visibleTabs(): TabConfig[] {
    return this.tabSearch.getVisibleTabs(this.tabs);
  }

  get objectsData() {
    return this.dialogData.objectsData;
  }

  ngOnInit(): void {
    this.setupUIState();
  }

  ngAfterViewInit(): void {
    this.tabSearch.setupAutoSwitch(this.tabs);
  }

  setupUIState() {
    this.fetchObjectsById()
      .pipe(
        tap((objects) => {
          this.objects = objects;

          // Calculate unique object type count and check for dimensions
          const ids = this.objects?.map((o) => o.object_type_id) ?? [];
          this.uniqueObjectTypeIdCount = new Set(ids).size;
          this.hasDimensionInSelection = ids.includes(
            NomObjectTypes.SEARCH_DIMENSION,
          );

          // Set appropriate title and icon based on selection
          const selectedCount = this.objects?.length ?? 0;
          if (this.hasDimensionInSelection === true) {
            if (this.uniqueObjectTypeIdCount > 1) {
              this.objectActionTitle = this.translateService.instant(
                'feed-duplicate-popup.title.copy-feeds-and-dimensions',
              );
              this.objectTitle = this.translateService.instant(
                `feed-duplicate-popup.title.selected-objects-count`,
                { count: selectedCount },
              );
            } else {
              this.objectActionTitle = this.translateService.instant(
                'feed-duplicate-popup.title.copy-dimensions',
              );
              this.objectTitle = this.objects[0]?.title;
              this.objectIcon = getIconForDataObject(
                this.objects[0]?.object_class_id,
                this.objects[0]?.object_type_id,
              );
            }
          } else {
            this.objectActionTitle = this.translateService.instant(
              'feed-duplicate-popup.title.copy-feeds',
            );
            this.objectTitle = this.objects[0]?.title;
            this.objectIcon = getIconForDataObject(
              this.objects[0]?.object_class_id,
              this.objects[0]?.object_type_id,
            );
          }

          // * setup TABS AFTER objects are fetched
          this.setupTabs();

          this.initializeBase();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  fetchObjectsById() {
    const requests = this.objectsData.map((data) =>
      this.utilsDataObjectApi.fetchObject(data.id, data.classId),
    );

    return forkJoin(requests);
  }

  setupTabs() {
    this.tabs.push({
      id: 'feed-duplicate-form',
      label: this.translateService.instant('feed-duplicate-popup.button.copy'),
      icon: ['far', 'clone'],
      component: FeedDuplicateFormComponent,
      inputs: {
        objects: this.objects,
        hasDimensionInSelection: this.hasDimensionInSelection,
      },
    });
  }

  performSave(formData: any): DialogCloseOptions<DialogResult> {
    const hasOption = (option: string) => formData.options?.includes(option);

    const copyFeedParams: CopyFeedParams = {
      description: formData.description,
      query_type_id: formData.object_type_id,
      object_ids: this.objects.map((object) => object.id),
      workspace_id: formData.workspace_id,

      duplicateAlerts: hasOption('duplicateAlerts'),
      duplicateWidgets: hasOption('duplicateWidgets'),
      duplicateExportSchedules: hasOption('duplicateExportSchedules'),
      duplicateOutputSettings: hasOption('duplicateOutputSettings'),
      duplicateContentFilters: hasOption('duplicateContentFilters'),
      duplicateRemovedItems: hasOption('duplicateRemovedItems'),
      preserveLinkedDimensions: hasOption('preserveLinkedDimensions'),
      duplicatePermissions: hasOption('duplicatePermissions'),
    };

    if (this.objects.length === 1) {
      copyFeedParams.title = formData.title;
    } else {
      copyFeedParams.prefix = formData.prefix;
    }

    this.feedPersistence.duplicate(copyFeedParams);

    return {
      reason: DialogCloseReason.Save,
      data: {},
    };
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    const formData = this.tabManager.combineValues();
    const closeOptions = this.performSave(formData);
    this.dirtyHandler.closeImmediately(closeOptions);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
